import type { ChatContext } from '@brain/contracts'
import { Button, cn, toast } from '@brain/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ChevronLeft, MessageSquare, NotebookPen, Plus, Send, Trash2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChatAnswer } from '../../components/chat/ChatAnswer.tsx'
import { ColumnHeader, ThreeCol } from '../../components/ThreeCol.tsx'
import { chatThreadQuery, chatThreadsQuery, deleteThread, saveThreadAsNote } from '../../lib/chat.ts'
import { jumpIndexQuery } from '../../lib/hooks.ts'
import { useChat } from '../../lib/use-chat.ts'

/** thread = open conversation · container = preselected context · q = ask right away (from the search page) ·
 * neu = empty new conversation (phones show the composer instead of the thread list) */
type Search = { thread?: string; container?: string; q?: string; neu?: true }

export const Route = createFileRoute('/_app/chat')({
  validateSearch: (s: Record<string, unknown>): Search => ({
    ...(typeof s.thread === 'string' ? { thread: s.thread } : {}),
    ...(typeof s.container === 'string' ? { container: s.container } : {}),
    ...(typeof s.q === 'string' && s.q.trim() ? { q: s.q } : {}),
    ...(s.neu === true || s.neu === 'true' || s.neu === 1 || s.neu === '1' ? { neu: true as const } : {}),
  }),
  component: Page,
})

const UUID = /^[0-9a-f-]{36}$/

/** Chat "Frag dein Brain" (§12.2): saved conversations, selectable context, sources and confirmation cards. */
function Page() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const search = Route.useSearch()
  const threadParam = search.thread && UUID.test(search.thread) ? search.thread : null
  // switching threads (list click, back button) – only when the URL changes, and not when our own stream just
  // created the thread (onThread below puts it into the URL)
  const lastParam = useRef(threadParam)
  const threads = useQuery(chatThreadsQuery())
  const index = useQuery(jumpIndexQuery())
  // a new thread from the first question → into the URL
  const chat = useChat(threadParam, {
    onThread: (id) => {
      lastParam.current = id
      void navigate({ to: '/chat', search: { thread: id }, replace: true })
    },
  })
  const detail = useQuery({ ...chatThreadQuery(threadParam ?? ''), enabled: Boolean(threadParam) })
  const [draft, setDraft] = useState('')
  const [contextKey, setContextKey] = useState<string>(
    search.container ? `container:${search.container}` : 'all',
  )
  const bottom = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (threadParam === lastParam.current) return
    lastParam.current = threadParam
    if (threadParam !== chat.threadId) chat.reset(threadParam)
  }, [threadParam, chat.threadId, chat.reset])
  useEffect(() => {
    if (detail.data && detail.data.thread.id === chat.threadId && !chat.messages.length && !chat.pending)
      chat.setMessages(detail.data.messages)
  }, [detail.data, chat.threadId, chat.messages.length, chat.pending, chat.setMessages])
  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll along with new messages and streamed text
  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' })
  }, [chat.messages.length, chat.pending?.content])
  // ?q= from the search page: ask once
  const asked = useRef(false)
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs once for the initial ?q=
  useEffect(() => {
    if (search.q && !threadParam && !asked.current) {
      asked.current = true
      void chat.send(search.q, { kind: 'all' })
    }
  }, [])

  const context: ChatContext =
    contextKey === 'all'
      ? { kind: 'all' }
      : contextKey.startsWith('para:')
        ? { kind: 'para', para: contextKey.slice(5) as 'project' | 'area' | 'resource' }
        : { kind: 'container', id: contextKey.slice(10) }
  const activeThread = detail.data?.thread ?? chat.thread
  const contextLabel = (c: ChatContext | undefined) => {
    if (!c || c.kind === 'all') return t('chat.context.all')
    if (c.kind === 'para') return t(`chat.context.para.${c.para}`)
    const found =
      index.data?.containers.find((x) => x.id === c.id) ?? index.data?.notes.find((x) => x.id === c.id)
    return found?.title ?? t('chat.context.container')
  }

  // phones show one column: the conversation once one is open, being started or being asked
  const showConversation = Boolean(
    threadParam || search.neu || search.q || chat.pending || chat.messages.length,
  )

  const submit = () => {
    if (!draft.trim()) return
    void chat.send(draft, context)
    setDraft('')
  }

  const list = (
    <>
      <ColumnHeader
        title={t('nav.chat')}
        actions={
          <Button
            size={32}
            variant="ghost"
            onClick={() => void navigate({ to: '/chat', search: { neu: true } })}
          >
            <Plus size={15} />
            {t('chat.new')}
          </Button>
        }
      />
      <div className="flex flex-col gap-0.5 px-2 pb-4">
        {threads.data?.length === 0 && (
          <p className="px-3 py-6 text-[13px] text-meta">{t('chat.noThreads')}</p>
        )}
        {threads.data?.map((th) => (
          <button
            key={th.id}
            type="button"
            onClick={() => void navigate({ to: '/chat', search: { thread: th.id } })}
            className={cn(
              'flex flex-col items-start gap-0.5 rounded-tile px-3 py-2 text-left hover:bg-grey-1',
              th.id === threadParam && 'bg-pop-tint',
            )}
          >
            <span className="w-full truncate text-[14px] text-text-1">{th.title || t('chat.untitled')}</span>
            <span className="text-[11px] text-meta">
              {new Date(th.updatedAt).toLocaleDateString('de-DE')} · {contextLabel(th.context)}
            </span>
          </button>
        ))}
      </div>
    </>
  )

  return (
    <ThreeCol list={list} mobile={showConversation ? 'content' : 'list'}>
      <ColumnHeader
        title={
          <span className="flex items-center gap-1">
            {/* phones: back to the thread list (desktop shows both columns) */}
            <Button
              size={32}
              variant="ghost"
              className="-ml-2 md:hidden"
              aria-label={t('chat.allChats')}
              title={t('chat.allChats')}
              onClick={() => void navigate({ to: '/chat', search: {} })}
            >
              <ChevronLeft size={18} />
            </Button>
            <span className="truncate">{activeThread?.title || t('chat.newTitle')}</span>
          </span>
        }
        actions={
          threadParam ? (
            <>
              <Button
                size={32}
                variant="ghost"
                title={t('chat.saveAsNote')}
                onClick={async () => {
                  const r = await saveThreadAsNote(threadParam)
                  toast(t('chat.savedAsNote'))
                  void navigate({ to: '/n/$noteId', params: { noteId: r.noteId } })
                }}
              >
                <NotebookPen size={15} />
              </Button>
              <Button
                size={32}
                variant="ghost"
                title={t('chat.delete')}
                onClick={async () => {
                  await deleteThread(threadParam)
                  void queryClient.invalidateQueries({ queryKey: ['chat'] })
                  void navigate({ to: '/chat', search: {} })
                }}
              >
                <Trash2 size={15} />
              </Button>
            </>
          ) : null
        }
      />
      <div className="mx-auto flex w-full max-w-[760px] flex-1 flex-col px-4 md:px-6">
        <div className="flex flex-1 flex-col gap-6 pb-6">
          {!threadParam && !chat.messages.length && !chat.pending && (
            <div className="flex flex-col items-center gap-2 py-16 text-center">
              <MessageSquare size={28} className="text-meta" />
              <p className="text-[15px] text-text-1">{t('chat.welcome')}</p>
              <p className="max-w-md text-[13px] text-text-2">{t('chat.welcomeText')}</p>
            </div>
          )}
          {chat.messages.map((m) =>
            m.role === 'user' ? (
              <div key={m.id} className="self-end rounded-card bg-pop-tint px-4 py-2 text-[14px] text-text-1">
                {m.content}
              </div>
            ) : (
              <ChatAnswer key={m.id} message={m} />
            ),
          )}
          {chat.pending && (
            <>
              {!chat.messages.some((m) => m.role === 'user' && m.content === chat.pending?.question) && (
                <div className="self-end rounded-card bg-pop-tint px-4 py-2 text-[14px] text-text-1">
                  {chat.pending.question}
                </div>
              )}
              <ChatAnswer
                message={{
                  content: chat.pending.content,
                  citations: [],
                  actions: [],
                  meta: chat.pending.error ? { error: chat.pending.error } : {},
                }}
                streaming={!chat.pending.error}
                status={chat.pending.status}
              />
            </>
          )}
          <div ref={bottom} />
        </div>
        <div className="sticky bottom-0 flex flex-col gap-2 bg-page pt-2 pb-4">
          {!threadParam && (
            <label className="flex items-center gap-2 text-[12px] text-text-2">
              {t('chat.context.label')}
              <select
                value={contextKey}
                onChange={(e) => setContextKey(e.target.value)}
                className="h-7 rounded-tile border border-line-2 bg-card px-2 text-[12px] text-text-1"
              >
                <option value="all">{t('chat.context.all')}</option>
                <option value="para:project">{t('chat.context.para.project')}</option>
                <option value="para:area">{t('chat.context.para.area')}</option>
                <option value="para:resource">{t('chat.context.para.resource')}</option>
                {index.data?.containers
                  .filter((c) => !c.archived)
                  .map((c) => (
                    <option key={c.id} value={`container:${c.id}`}>
                      {c.title}
                    </option>
                  ))}
              </select>
            </label>
          )}
          {threadParam && activeThread && (
            <span className="text-[12px] text-meta">
              {t('chat.context.label')} {contextLabel(activeThread.context)}
            </span>
          )}
          <div className="flex items-end gap-2 rounded-card border border-line-2 bg-card p-2 focus-within:border-pop">
            <textarea
              value={draft}
              rows={Math.min(6, Math.max(1, draft.split('\n').length))}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                  e.preventDefault()
                  submit()
                }
              }}
              placeholder={t('chat.placeholder')}
              aria-label={t('chat.placeholder')}
              className="max-h-40 min-h-9 flex-1 resize-none bg-transparent px-2 py-1.5 text-[14px] text-text-1 outline-none placeholder:text-meta"
            />
            <Button
              size={36}
              variant="primary"
              disabled={!draft.trim() || Boolean(chat.pending && !chat.pending.error)}
              onClick={submit}
              aria-label={t('chat.send')}
            >
              <Send size={15} />
            </Button>
          </div>
        </div>
      </div>
    </ThreeCol>
  )
}
